import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { EASE } from "./Reveal";
import { LineField } from "./Interactive";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Rendered as the top band of the Footer — the final invitation.
const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/newsletter`, { email: email.trim(), website });
      setSent(true);
      setEmail("");
    } catch (err) {
      setError("Something went quiet on our end. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="newsletter"
      className="grid grid-cols-1 gap-8 border-b border-gaia-bg/15 pb-16 md:grid-cols-2 md:items-end md:gap-16"
      data-testid="newsletter-section"
    >
      <div>
        <span className="font-sans text-[11px] uppercase tracking-[0.28em] text-gaia-terracotta">The Invitation</span>
        <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-gaia-bg md:text-5xl" data-testid="newsletter-heading">
          Stay Connected
        </h2>
        <p className="mt-4 max-w-md font-sans text-base font-light leading-relaxed text-gaia-bg/60">
          Receive occasional reflections, future gatherings, and quiet inspiration from Gaia Space.
        </p>
      </div>

      <div className="w-full md:max-w-md md:justify-self-end">
        <AnimatePresence mode="wait">
          {sent ? (
            <motion.p
              key="thanks"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="font-serif text-2xl font-light italic text-gaia-terracotta"
              data-testid="newsletter-success"
            >
              Thank you. We'll write to you softly.
            </motion.p>
          ) : (
            <motion.form
              key="form"
              onSubmit={submit}
              exit={{ opacity: 0 }}
              className="flex items-end gap-4"
              data-testid="newsletter-form"
            >
              <div className="flex-1">
                <LineField
                  tone="onDark"
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  testId="newsletter-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-btn"
                className="shrink-0 bg-gaia-terracotta px-8 py-[15px] font-sans text-[11px] uppercase tracking-[0.24em] text-gaia-bg transition-all duration-500 hover:bg-gaia-terracotta/85 disabled:opacity-50"
              >
                {loading ? "Joining…" : "Join"}
              </button>
              <div aria-hidden="true" className="sr-only">
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  data-testid="newsletter-honeypot-input"
                />
              </div>
            </motion.form>
          )}
        </AnimatePresence>
        {error && (
          <p className="mt-4 font-sans text-sm font-light text-gaia-terracotta" data-testid="newsletter-error">{error}</p>
        )}
      </div>
    </div>
  );
};

export default Newsletter;
